import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, RotateCw, ArrowRight } from "lucide-react";
import { Stage } from "@/components/Stage";
import { Eyebrow } from "@/components/Wordmark";

const EASE = [0.22, 1, 0.36, 1];

const CARDS = [
  {
    quote: "It made more work",
    frontTitle: "You audited every call by hand",
    frontBody: "To catch what it missed, someone still listened to all of it.",
    tag: "Auto-reviewed",
    backTitle: "You skim exceptions, not everything",
    backBody:
      "Every call reviewed automatically — irate callers, wrong info, missed transfers, all flagged.",
  },
  {
    quote: "It sounded like a bot",
    frontTitle: "Callers knew in seconds",
    frontBody: "A generic voice — sometimes saying your firm name wrong.",
    tag: "Tuned to your firm",
    backTitle: "Your greeting, your questions, your name",
    backBody:
      "Agents are tuned to how your firm actually sounds — down to how the name is said.",
  },
  {
    quote: "It ignored our stack",
    frontTitle: "Nothing wrote back",
    frontBody: "It sat outside your CMS, so someone re-keyed every detail.",
    tag: "Native to your CMS",
    backTitle: "Data stays where it already lives",
    backBody:
      "Native to Litify, Filevine, SmartAdvocate, Salesforce — logged in real time.",
  },
  {
    quote: "We couldn't trust it",
    frontTitle: "One bad call wasn't worth it",
    frontBody: "A client in crisis is no place for a hallucination.",
    tag: "You stay in control",
    backTitle: "You set the confidence line",
    backBody:
      "High-confidence handles it; anything uncertain hands to a human — instantly.",
  },
];

const FlipCard = ({ card, index, flipped, onToggle, variants }) => {
  const idx = String(index + 1).padStart(2, "0");
  return (
    <motion.div variants={variants} className="flip-card h-full">
      <div
        role="button"
        tabIndex={0}
        aria-pressed={flipped}
        aria-label={`${card.frontTitle}. See the fix.`}
        data-testid={`flip-card-${index + 1}`}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        className="h-full rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[#F7F6F5] cursor-pointer"
      >
        <div className={`flip-inner ${flipped ? "is-flipped" : ""}`}>
          {/* FRONT */}
          <div className="flip-face rounded-2xl border border-hairline bg-[#FBFAF6] p-5 flex flex-col shadow-[0_1px_3px_rgba(35,31,35,0.05),0_10px_28px_-14px_rgba(35,31,35,0.18)]">
            <span className="font-mono text-xs text-accent tabular-nums">
              {idx}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-muted mt-3">
              “{card.quote}”
            </span>
            <h3 className="font-display font-medium text-lg leading-snug text-ink mt-2">
              {card.frontTitle}
            </h3>
            <p className="font-sans text-[13px] text-ink-soft leading-relaxed mt-2">
              {card.frontBody}
            </p>
            <span className="mt-auto pt-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-accent">
              See the fix
              <RotateCw className="w-3 h-3" />
            </span>
          </div>

          {/* BACK */}
          <div className="flip-face flip-back rounded-2xl p-5 flex flex-col text-paper bg-[linear-gradient(155deg,#231F23_0%,#2E2A31_100%)]">
            <span className="inline-flex items-center gap-2 self-start rounded-full bg-white/[0.08] border border-white/15 pl-1 pr-3 py-1">
              <span className="w-4 h-4 rounded-full bg-accent flex items-center justify-center shrink-0">
                <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#93B4FF]">
                {card.tag}
              </span>
            </span>
            <h3 className="font-display font-medium text-lg leading-snug text-paper mt-4">
              {card.backTitle}
            </h3>
            <p className="font-sans text-[13px] text-paper/70 leading-relaxed mt-2">
              {card.backBody}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const ReframeSlide = ({ slide }) => {
  const reduce = useReducedMotion();
  const [flipped, setFlipped] = useState({});

  const toggle = (i) =>
    setFlipped((prev) => ({ ...prev, [i]: !prev[i] }));

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.12, delayChildren: 0.06 } },
  };
  const item = {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };
  const cardsRow = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.11 } },
  };
  const cardItem = {
    hidden: { opacity: 0, y: reduce ? 0 : 22 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
  };

  return (
    <Stage testId={`slide-${slide?.n ?? 3}`}>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="h-full w-full flex flex-col px-[clamp(28px,6%,84px)] py-[clamp(24px,5%,52px)]"
      >
        {/* Top row */}
        <motion.div variants={item} className="flex items-center justify-between">
          <Eyebrow>Why it hasn't worked</Eyebrow>
          <span className="font-mono text-xs tracking-[0.24em] text-ink-muted tabular-nums">
            04 / 11
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          variants={item}
          className="font-display font-medium text-3xl sm:text-4xl leading-[1.06] text-ink mt-5 max-w-[18ch]"
        >
          You've tried AI before. It made more work, not less.
        </motion.h1>

        {/* Subhead */}
        <motion.p
          variants={item}
          className="font-sans text-base text-ink-soft leading-relaxed mt-4 max-w-[56ch]"
        >
          You were right to be skeptical — most legal AI wasn't built for how a
          firm actually runs.{" "}
          <span className="text-accent">
            Tap a card to see what should have happened instead.
          </span>
        </motion.p>

        {/* Cards */}
        <motion.div
          variants={cardsRow}
          className="flex-1 min-h-0 grid grid-cols-1 min-[520px]:grid-cols-2 min-[900px]:grid-cols-4 gap-4 mt-7"
        >
          {CARDS.map((card, i) => (
            <FlipCard
              key={i}
              index={i}
              card={card}
              flipped={!!flipped[i]}
              onToggle={() => toggle(i)}
              variants={cardItem}
            />
          ))}
        </motion.div>

        {/* Reframe footer */}
        <motion.div
          variants={item}
          className="border-t border-hairline pt-5 mt-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3"
        >
          <p className="font-display font-medium text-xl sm:text-2xl leading-snug text-ink max-w-3xl">
            The problem was never AI. It was AI built by people who've{" "}
            <em className="italic text-accent">never worked in the legal domain.</em>
          </p>
          <span className="inline-flex items-center gap-1.5 font-mono text-sm uppercase tracking-[0.12em] text-accent whitespace-nowrap">
            We have.
            <ArrowRight className="w-4 h-4" />
          </span>
        </motion.div>
      </motion.div>
    </Stage>
  );
};

export default ReframeSlide;
